import { createApi } from '@reduxjs/toolkit/query/react';
import type { IntakeSummaryRecord } from '@hubertai/screening-question-types';
import z from 'zod';
import { ChatMessageSchema, toSessionMessage } from './chat.message';
import type { SessionMessage } from './chat.message';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';
import config from 'config/index';
import { EUserType } from 'types/accessControll.types';
import type { TAxiosBaseResponseSuccess } from 'rtk/query/rtk-query.types';
import { SESSION_FLOW } from 'components/AgentChat/chat/types/session.types';
import type { SessionFlow } from 'components/AgentChat/chat/types/session.types';

export type { SessionMessage } from './chat.message';

export const SessionResponseSchema = z.object({
  sessionId: z.string(),
  title: z.string(),
  // Absent on a session created before the flows split, and those are legacy.
  flow: z.enum([SESSION_FLOW.Legacy, SESSION_FLOW.Blueprint]).nullable().optional(),
  handoffMode: z.enum(['auto-build', 'scope-choice']).nullable().optional(),
  updatedAt: z.string(),
  // Whether a turn is running on this session right now, as manager-agent reports it from the
  // advisory lock a run holds. True whichever instance is running the turn, which is what the
  // sidebar seeds its working marker from before the socket has said anything.
  isGenerating: z.boolean().optional(),
  archivedAt: z.string().nullable().optional(),
  deletedAt: z.string().nullable().optional(),
  messages: z.array(ChatMessageSchema).nullable().optional(),
  hasMore: z.boolean().nullable().optional(),
  nextBeforeTs: z.number().nullable().optional(),
  nextBeforeId: z.string().nullable().optional(),
});

export const PaginatedSessionsResponseSchema = z.object({
  items: z.array(SessionResponseSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  page_size: z.number().int().positive(),
  total_pages: z.number().int().nonnegative(),
});

export type SessionResponse = z.infer<typeof SessionResponseSchema>;

export { SESSION_FLOW };
export type { SessionFlow };

export type SessionUi = SessionResponse & {
  active: boolean;
};
export interface SessionCreateDto {
  title?: string | null;
}

export interface SessionUpdateDto {
  title?: string | null;
}

export type SessionResponseWithMessages = Omit<SessionResponse, 'messages'> & {
  messages?: SessionMessage[];
  hasMore?: boolean | null;
  nextBeforeTs?: number | null;
  nextBeforeId?: string | null;
};

interface UploadFileResponse {
  artifacts: Array<{ filename: string; mime_type: string; version: number; url: string }>;
}

type ListSessionsArgs = {
  includeArchived?: boolean;
  includeDeleted?: boolean;
  searchQuery?: string;
  page?: number;
  pageSize?: number;
};

export type PaginatedSessionsResponse = {
  items: SessionResponse[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};
type ListSessionsApiResponse = {
  items: SessionResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
};

const reducerPath = 'agent-chat' as const;

/**
 * A conversation's history, as distinct from the session row the sidebar lists.
 *
 * Its own tag so that writing to the history refetches the history and nothing else: `Session` is
 * provided by the session list too, and every card action would otherwise reload the sidebar.
 */
const sessionHistory = (sessionId: string) => [{ type: 'Message' as const, id: sessionId }];

/** How many messages a page of a conversation's history holds. */
export const MESSAGE_PAGE_SIZE = 10;

/** Where a page of history ends: the messages before this one. Null for the newest page. */
type SessionHistoryCursor = { beforeTs: number; beforeId: string } | null;
const path = 'v1/agent-chat' as const;

/**
 * The session as the chat reads it.
 *
 * Field by field rather than a spread, which is how `flow` came to be missing from it: the type this
 * returns has every field but `sessionId`, `title` and `updatedAt` optional, so a field left out here
 * costs nothing at compile time and everything at runtime. Anything the page needs has to be listed.
 *
 * Exported so it can be tested without standing up the store — see chat.transform.spec.ts.
 */
export function toSessionWithMessages(apiResponse: unknown): SessionResponseWithMessages {
  const parsed = SessionResponseSchema.parse(apiResponse);

  return {
    sessionId: parsed.sessionId,
    title: parsed.title,
    // The chat renders from this: a blueprint session has no review card, because its template
    // is already being built. Dropping it here is invisible to TypeScript — the field is
    // optional — and shows up as the review step reappearing in a flow that has none.
    flow: parsed.flow ?? null,
    updatedAt: parsed.updatedAt,
    archivedAt: parsed.archivedAt ?? null,
    deletedAt: parsed.deletedAt ?? null,
    hasMore: parsed.hasMore ?? null,
    nextBeforeTs: parsed.nextBeforeTs ?? null,
    nextBeforeId: parsed.nextBeforeId ?? null,
    messages: parsed.messages?.map(toSessionMessage),
  };
}

/**
 * Writes a change to one session into every cached copy of it: the conversation's header and each
 * sidebar page that lists it. The single place a title (or any row field) is updated locally, so the
 * header and the sidebar cannot disagree.
 */
export const patchSessionInCache =
  (sessionId: string, patch: Partial<Pick<SessionResponse, 'title' | 'isGenerating' | 'updatedAt'>>) =>
  (dispatch: (action: unknown) => any, getState: () => any) => {
    const state = getState();
    const patches = [];
    const { isGenerating: _isGenerating, ...historyPatch } = patch;
    for (const arg of agentChatApi.util.selectCachedArgsForQuery(state, 'getSessionHistory')) {
      if (arg.sessionId !== sessionId || Object.keys(historyPatch).length === 0) continue;
      patches.push(
        dispatch(
          agentChatApi.util.updateQueryData('getSessionHistory', arg, (draft) => {
            for (const page of draft.pages) Object.assign(page, historyPatch);
          }),
        ),
      );
    }
    for (const arg of agentChatApi.util.selectCachedArgsForQuery(state, 'listSessions')) {
      patches.push(
        dispatch(
          agentChatApi.util.updateQueryData('listSessions', arg, (draft) => {
            for (const row of draft.items) if (row.sessionId === sessionId) Object.assign(row, patch);
          }),
        ),
      );
    }
    return patches as Array<{ undo: () => void }>;
  };

export const agentChatApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  tagTypes: ['Session', 'Message'],
  endpoints: (builder) => ({
    createSession: builder.mutation<SessionResponse, SessionCreateDto>({
      query: (body) => ({
        url: '/sessions',
        method: 'POST',
        data: body,
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),
      /**
       * Seeds what the new conversation will be read from, so opening it shows the empty conversation
       * at once instead of a loading state, and the sidebar lists it without waiting on a refetch.
       *
       * Only the first page of the plain sidebar list is written to: a search or an archive view
       * has its own idea of what belongs in it, and those refetch through the invalidation below.
       */
      async onQueryStarted(_body, { dispatch, getState, queryFulfilled }) {
        let created: SessionResponse;
        try {
          created = (await queryFulfilled).data;
        } catch {
          return;
        }

        dispatch(
          agentChatApi.util.upsertQueryData(
            'getSessionHistory',
            { sessionId: created.sessionId, messageLimit: MESSAGE_PAGE_SIZE },
            {
              pages: [
                {
                  sessionId: created.sessionId,
                  title: created.title,
                  flow: created.flow ?? null,
                  updatedAt: created.updatedAt,
                  archivedAt: null,
                  deletedAt: null,
                  hasMore: false,
                  nextBeforeTs: null,
                  nextBeforeId: null,
                  messages: [],
                },
              ],
              pageParams: [null],
            },
          ),
        );

        for (const arg of agentChatApi.util.selectCachedArgsForQuery(getState(), 'listSessions')) {
          if ((arg.page ?? 1) !== 1 || arg.searchQuery || arg.includeArchived || arg.includeDeleted) continue;
          dispatch(
            agentChatApi.util.updateQueryData('listSessions', arg, (draft) => {
              if (draft.items.some((item) => item.sessionId === created.sessionId)) return;
              draft.items.unshift({
                sessionId: created.sessionId,
                title: created.title,
                updatedAt: created.updatedAt,
                archivedAt: null,
                deletedAt: null,
                isGenerating: false,
              });
              draft.total += 1;
              if (arg.pageSize && draft.items.length > arg.pageSize) draft.items.pop();
            }),
          );
        }
      },
      // No invalidation: the row is inserted above. Refetching the list here is what made the sidebar
      // reload on the first send of every new chat.
    }),
    listSessions: builder.query<PaginatedSessionsResponse, ListSessionsArgs>({
      query: ({ includeArchived, includeDeleted, searchQuery, page = 1, pageSize = 5 } = {}) => ({
        url: '/sessions',
        method: 'GET',
        params: {
          include_archived: includeArchived,
          include_deleted: includeDeleted,
          search_query: searchQuery || undefined,
          page,
          page_size: pageSize,
        },
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),

      transformResponse: (baseQueryReturnValue: TAxiosBaseResponseSuccess): PaginatedSessionsResponse => {
        const apiResponse = (baseQueryReturnValue as any)?.data ?? (baseQueryReturnValue as any);

        // Basic shape check (optional, but consistent with your getSession)
        if (!apiResponse?.items || typeof apiResponse?.total !== 'number') {
          throw new Error('Invalid listSessions response shape');
        }

        const typed = apiResponse as ListSessionsApiResponse;

        return {
          items: typed.items.map((s) => ({
            sessionId: s.sessionId,
            title: s.title,
            updatedAt: s.updatedAt,
            isGenerating: s.isGenerating ?? false,
            archivedAt: s.archivedAt ?? null,
            deletedAt: s.deletedAt ?? null,
          })),
          total: typed.total,
          page: typed.page,
          pageSize: typed.page_size,
          totalPages: typed.total_pages,
        };
      },

      providesTags: (result) =>
        result
          ? [
              { type: 'Session', id: 'LIST' },
              ...result.items.map((s) => ({ type: 'Session' as const, id: s.sessionId })),
            ]
          : [{ type: 'Session', id: 'LIST' }],
    }),

    /**
     * A conversation, newest messages first, a page at a time.
     *
     * Each page is the session as the backend answers it — the row plus a page of messages — so the
     * title and flow are read off whichever page is newest. Paged by cursor: a page is "the messages
     * before this one", which is why a refetch has to redo every page rather than only the first. New
     * messages move where the newest page ends, and older pages kept as they were would leave a gap
     * between it and them. RTK Query does that by default, rebuilding each cursor from the page it has
     * just fetched.
     */
    getSessionHistory: builder.infiniteQuery<
      SessionResponseWithMessages,
      { sessionId: string; messageLimit: number },
      SessionHistoryCursor
    >({
      // Long turns outlive the default one-minute cache window while another chat is open.
      keepUnusedDataFor: 10 * 60,
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (newestFetched) =>
          newestFetched.hasMore && newestFetched.nextBeforeTs != null && newestFetched.nextBeforeId
            ? { beforeTs: newestFetched.nextBeforeTs, beforeId: newestFetched.nextBeforeId }
            : undefined,
      },
      query: ({ queryArg: { sessionId, messageLimit }, pageParam }) => ({
        url: `/sessions/${sessionId}`,
        method: 'GET',
        params: {
          include_messages: true,
          message_limit: messageLimit,
          before_ts: pageParam?.beforeTs,
          before_id: pageParam?.beforeId,
        },
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),

      transformResponse: (baseQueryReturnValue: TAxiosBaseResponseSuccess): SessionResponseWithMessages =>
        toSessionWithMessages((baseQueryReturnValue as any)?.data ?? baseQueryReturnValue),

      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        try {
          const session = (await queryFulfilled).data.pages[0];
          if (!session) return;
          // A title event may have been missed while another conversation was open.
          patchSessionInCache(session.sessionId, { title: session.title })(dispatch, getState);
        } catch {
          // Failed history requests leave the existing sidebar titles intact.
        }
      },

      // Only its own tag: a session-row change (rename, list refresh) must not refetch the messages.
      providesTags: (_result, _error, { sessionId }) => sessionHistory(sessionId),
    }),

    updateSession: builder.mutation<SessionResponse, { sessionId: string; body: SessionUpdateDto }>({
      query: ({ sessionId, body }) => ({
        url: `/sessions/${sessionId}`,
        method: 'PATCH',
        data: body,
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),
      /**
       * Shows the new title everywhere at once — the conversation's header and the sidebar — and puts
       * the old one back in both if the rename fails. Written into the cache rather than held beside
       * it, so the refetch the invalidation then makes is what has the last word: a title the agent
       * set in the meantime replaces this one instead of being hidden behind it.
       */
      async onQueryStarted({ sessionId, body }, { dispatch, getState, queryFulfilled }) {
        const title = body.title?.trim();
        if (!title) return;

        const patches = patchSessionInCache(sessionId, { title })(dispatch, getState);

        try {
          await queryFulfilled;
        } catch {
          for (const patch of patches) patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { sessionId }) => [
        { type: 'Session', id: sessionId },
        { type: 'Session', id: 'LIST' },
      ],
    }),

    deleteSession: builder.mutation<void, string>({
      query: (sessionId) => ({
        url: `/sessions/${sessionId}`,
        method: 'DELETE',
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),
      invalidatesTags: (_result, _error, sessionId) => [
        { type: 'Session', id: sessionId },
        { type: 'Session', id: 'LIST' },
      ],
    }),

    uploadFiles: builder.mutation<
      Array<{ filename: string; version: number; url: string }>,
      { sessionId: string; files: File[] }
    >({
      query: ({ sessionId, files }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append('files', file);
        });
        return {
          url: `/message/${sessionId}/upload`,
          method: 'POST',
          data: formData,
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
      transformResponse: (baseQueryReturnValue: TAxiosBaseResponseSuccess) => {
        const response = baseQueryReturnValue.data as UploadFileResponse;
        return response.artifacts.map(({ filename, version, url }) => ({
          filename,
          version,
          url,
        }));
      },
    }),

    // ── Blueprint flow ──────────────────────────────────────────────────────
    // Blueprint sessions build directly instead of asking the user to choose a matching template.

    /** Step 2: propose the questions. Posts the blueprint into the chat; returns nothing but success. */
    generateBlueprint: builder.mutation<
      { success: true },
      {
        sessionId: string;
        structuredRecord: IntakeSummaryRecord;
        parentMessageId: string;
        clientId?: string;
        language?: string;
        readyMessage?: string;
        /**
         * The access groups the template will belong to.
         *
         * Sent with the run rather than with the approval: the blueprint can only reference library
         * questions its template is allowed to use, so the run has to know the scope up front.
         */
        accessGroupIds?: string[];
        /** Where the template will live: Hubert's library, one organization, or access groups in it. */
        visibility?: 'global' | 'client' | 'local';
      }
    >({
      query: ({ sessionId, ...body }) => ({
        url: `/blueprints/${sessionId}/generate`,
        method: 'POST',
        data: body,
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),
      // A 409 is the run already under way or finished, which is the ordinary answer whenever the
      // page missed the announce that its card had landed. Re-reading the history is what puts that
      // card on the page, so it is fetched on a 409 as much as on success.
      invalidatesTags: (_result, error, { sessionId }) =>
        !error || (error as { status?: unknown }).status === 409 ? sessionHistory(sessionId) : [],
    }),
  }),
});

export const {
  useCreateSessionMutation,
  useListSessionsQuery,
  useGetSessionHistoryInfiniteQuery,
  useUpdateSessionMutation,
  useDeleteSessionMutation,
  useUploadFilesMutation,
  useGenerateBlueprintMutation,
} = agentChatApi;
